import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { type OccupantType } from 'restaurantAdmin/reservations/apiHelpers';
import { useRestaurant } from '../../../context/useRestaurant';
import {
  getServiceReservationDetails,
  getServiceWalkInDetails,
  type ServiceOccupant,
} from '../apiHelpers';
import { useQuery } from '@tanstack/react-query';

interface OccupantParams {
  id: string;
  type: OccupantType;
}

export const useFetchOccupantDetails = (
  occupantParams: OccupantParams | null,
) => {
  const { id: restaurantId } = useRestaurant();

  const {
    data: occupant,
    refetch: invalidate,
    ...rest
  } = useQuery({
    queryKey: [
      'restaurants',
      restaurantId,
      'occupants',
      occupantParams?.type,
      occupantParams?.id,
    ],
    queryFn: async () => {
      if (!occupantParams) return null;
      const { id: occupantId, type: occupantType } = occupantParams;
      const getOccupantDetails = getOccupantDetailsFunction(occupantType);
      return getOccupantDetails(restaurantId, occupantId);
    },
    throwOnError: true,
    enabled: occupantParams !== null,
  });

  return { occupant, invalidate, ...rest };
};

type GetOccupantDetails = (
  restaurantId: string,
  occupantId: string,
) => Promise<ServiceOccupant>;

const getOccupantDetailsFunction = (
  occupantType: string,
): GetOccupantDetails => {
  if (occupantType === 'reservation') return getServiceReservationDetails;
  if (occupantType === 'walkIn') return getServiceWalkInDetails;
  throw new TypeError('occupantType fallthrough');
};
